import { Chip, Icon, Text } from "@assetwatch/ui";
import { useTranslation } from "react-i18next";
import {
  formatMarketplaceCurrency,
  formatMarketplaceDate,
  getMarketplaceIcon,
} from "../../lib/marketplaceFormat";
import { toExpansionRequest } from "../../lib/marketplaceRequests";
import type {
  MarketplaceSubscription,
  SelectedMarketplaceRequest,
} from "../../types/marketplace";
import { MarketplaceCard } from "./MarketplaceCard";

type ActiveSubscriptionCardProps = {
  subscription: MarketplaceSubscription;
  onSelect: (request: SelectedMarketplaceRequest) => void;
};

export function ActiveSubscriptionCard({
  subscription,
  onSelect,
}: ActiveSubscriptionCardProps) {
  const { t, i18n } = useTranslation();

  return (
    <MarketplaceCard
      title={subscription.name}
      type={subscription.type}
      description={subscription.description}
      icon={getMarketplaceIcon(subscription.type, subscription.name)}
      onClick={() => onSelect(toExpansionRequest(subscription))}
    >
      <div className="grid grid-cols-2 gap-(--spacing-12) border-t border-outline-subtle pt-(--spacing-12)">
        <div className="w-fit">
          <Chip
            tone="positive"
            icon="circle-check"
            label={t("marketplace.active")}
          />
        </div>
        <Text size="sm">
          {t("marketplace.startDate", {
            date: formatMarketplaceDate(
              subscription.startDate,
              t("marketplace.notApplicable"),
              i18n.language,
            ),
          })}
        </Text>
        <div
          className="flex items-center gap-(--spacing-8)"
          title={t("marketplace.facilities", {
            count: subscription.totalFacilities,
          })}
        >
          <Icon name="facilities" size="sm" tone="secondary" aria-hidden />
          <Text size="sm">{subscription.totalFacilities}</Text>
        </div>
        <div
          className="flex items-center gap-(--spacing-8)"
          title={t("marketplace.monitoringPoints", {
            count: subscription.totalMonitoringPoints,
          })}
        >
          <Icon
            name="monitoring-point"
            size="lg"
            tone="secondary"
            aria-hidden
          />
          <Text size="sm">{subscription.totalMonitoringPoints}</Text>
        </div>
        <Text size="sm">
          {t("marketplace.downtimePrevented", {
            count: subscription.downtimePrevented,
          })}
        </Text>
        <Text size="sm">
          {t("marketplace.costSavings", {
            amount: formatMarketplaceCurrency(
              subscription.costSavings,
              i18n.language,
            ),
          })}
        </Text>
      </div>
    </MarketplaceCard>
  );
}
